<script setup lang="ts">
import { ref, computed, type ComponentPublicInstance } from 'vue'
import useApi from '../../Composables/useApi'
import { useI18n } from 'vue-i18n'
import DataTable from '../UI/Table/DataTable.vue'
import type { DocumentSign, DocumentSignFile } from '../../../../Types/document'
import type { TableColumn } from '../../../../Types/table'

interface DocumentSignListItem {
  id: number
  context: string
  createdAt: string
  documentSignFiles: string
  status: string
  expiresAt: string
}

type LoadDocumentSignsCallback = (options: { data: DocumentSignListItem[] }) => void

const props = defineProps<{
  contractId: number
}>()

const emit = defineEmits<{
  (event: 'open-document-sign', documentSignId: number): void
}>()

const { getJSON } = useApi()
const { t } = useI18n()

const dataTableRef = ref<ComponentPublicInstance | null>(null)

const dataTableOptions = {
  ordering: false,
  searching: false,
  lengthChange: false,
  info: false,
  rowClickable: true,
}

const columns = computed<TableColumn<DocumentSignListItem>[]>(() => [
  {
    name: 'context',
    title: t('contract.document.documentSign.context'),
    formatter: 'custom',
    style: { className: 'text-center' },
    options: {
      renderer: renderContext,
    },
  },
  {
    name: 'createdAt',
    title: t('contract.document.documentSign.createdAt'),
    formatter: 'date',
    style: { className: 'text-center' },
  },
  {
    name: 'documentSignFiles',
    title: t('contract.document.documentSign.documents'),
    style: { className: 'text-center' },
  },
  {
    name: 'status',
    title: t('document.table.labelStatus'),
    formatter: 'custom',
    style: { className: 'text-center' },
    options: {
      renderer: renderStatus,
    },
  },
  {
    name: 'expiresAt',
    title: t('contract.document.documentSign.expiresAt'),
    formatter: 'date',
    style: { className: 'text-center' },
  },
])

function renderStatus(data: string): string {
  return data ? t(`contract.document.documentSign.status.${data}`) : '-'
}

function renderContext(data: string): string {
  return t(`entity.context.${data}`)
}

function onRowClick(documentSign: DocumentSignListItem): void {
  if (documentSign && documentSign.status !== 'CANCELLED') {
    emit('open-document-sign', documentSign.id)
  }
}

async function loadDocumentSigns(callback: LoadDocumentSignsCallback): Promise<void> {
  const data: DocumentSign[] = await getJSON(`contracts/${props.contractId}/document-signs`)
  const transformedData: DocumentSignListItem[] = (data || []).map((documentSign: DocumentSign) => {
    return {
      ...documentSign,
      documentSignFiles:
        documentSign.documentSignFiles
          .map((documentSignFile: DocumentSignFile) => documentSignFile.document?.documentType?.name)
          .filter(Boolean)
          .join(', ') || '-',
    }
  })
  callback({ data: transformedData })
}
</script>

<template>
  <DataTable
    id="documentSignList"
    ref="dataTableRef"
    :fetch="loadDocumentSigns"
    :columns="columns"
    :options="dataTableOptions"
    :paginate="false"
    @row-click="onRowClick"
  />
</template>
